// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Form, Button, message } from 'antd';
import { ArrowLeft, User, Wallet, CreditCard, Phone, Briefcase, Users, GraduationCap, Building2, Award, BookOpen, FolderOpen, Save, XCircle } from 'lucide-react';
import type { MenuProps } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { usersService } from '../services/users.service';
import { useUpdatePersonalInfo } from '@/features/personnel/useUpdatePersonalInfo';
import { UpdateProfileLayout } from '@/components/profile/UpdateProfileLayout';
import { UpdatePersonalInfoSection } from '@/components/profile/UpdatePersonalInfoSection';
import { UpdateHoroscopeSection } from '@/components/profile/UpdateHoroscopeSection';
import { UpdateIdDocumentsSection } from '@/components/profile/UpdateIdDocumentsSection';
import { UpdateContactInfoSection } from '@/components/profile/UpdateContactInfoSection';
import { UpdateBankInfoSection } from '@/components/profile/UpdateBankInfoSection';
import { UpdateWorkInfoSection } from '@/components/profile/UpdateWorkInfoSection';
import { UpdateFamilyDependentsSection } from '@/components/profile/UpdateFamilyDependentsSection';
import { UpdateEducationSection } from '@/components/profile/UpdateEducationSection';
import { UpdateCertificatesSkillsSection } from '@/components/profile/UpdateCertificatesSkillsSection';
import { UpdatePartyHistorySection } from '@/components/profile/UpdatePartyHistorySection';
import { UpdateFileStorageSection } from '@/components/profile/UpdateFileStorageSection';
import styles from './UpdatePersonalProfilePage.module.css';

// Menu titles mapping
const MENU_TITLES: Record<string, string> = {
  'personal-info': 'Thông tin cá nhân',
  'horoscope': 'Lá số tử vi',
  'id-documents': 'Thông tin CC/CCCD/Hộ chiếu',
  'contact-info': 'Thông tin liên hệ',
  'work-info': 'Thông tin công việc',
  'family': 'Gia đình và người phụ thuộc',
  'education': 'Quá trình học tập',
  'bank-info': 'Thông tin ngân hàng',
  'certificates': 'Chứng chỉ & Kỹ năng',
  'party-history': 'Lịch sử Đảng viên',
  'file-storage': 'Kho lưu trữ file cá nhân',
};

export const UpdatePersonalProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [certificateAttachmentUploaded, setCertificateAttachmentUploaded] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [horoscopePreview, setHoroscopePreview] = useState<string | null>(null);
  const [horoscopeUploading, setHoroscopeUploading] = useState(false);
  const [horoscopeSelectedCount, setHoroscopeSelectedCount] = useState(0);
  const [isHoroscopeDirty, setIsHoroscopeDirty] = useState(false);
  const [horoscopeFile, setHoroscopeFile] = useState<File | null>(null);
  const [citizenIdFrontPreview, setCitizenIdFrontPreview] = useState<string | null>(null);
  const [citizenIdBackPreview, setCitizenIdBackPreview] = useState<string | null>(null);
  const [passportPreview, setPassportPreview] = useState<string | null>(null);
  const [passportAdditionalPreview, setPassportAdditionalPreview] = useState<string | null>(null);
  const [idDocsUploading, setIdDocsUploading] = useState(false);
  const [isIdDocsDirty, setIsIdDocsDirty] = useState(false);
  const [citizenIdFrontFile, setCitizenIdFrontFile] = useState<File | null>(null);
  const [citizenIdBackFile, setCitizenIdBackFile] = useState<File | null>(null);
  const [passportCoverFile, setPassportCoverFile] = useState<File | null>(null);
  const [passportPhotoFile, setPassportPhotoFile] = useState<File | null>(null);

  // Sử dụng hook useUpdatePersonalInfo - không truyền personnelData (chỉ dành cho user hiện tại)
  const {
    form,
    loading,
    initialDataStatus,
    uploading,
    profileImage,
    setProfileImage,
    sidebarCollapsed,
    setSidebarCollapsed,
    selectedMenuKey,
    isHovered,
    setIsHovered,
    handleSubmit: hookHandleSubmit,
    handleValuesChange,
    handleCancel,
    handleImageUpload,
    handleMenuClick,
  } = useUpdatePersonalInfo();

  // Kiểm tra xem có yêu cầu cập nhật đang pending không
  useEffect(() => {
    const checkPendingRequest = async () => {
      if (!user?.id) return;

      try {
        const userId = parseInt(user.id.toString(), 10);
        if (isNaN(userId)) return;

        const response = await usersService.getUpdateRequests({
          userId,
          status: 'pending',
          limit: 1,
        });

        setHasPendingRequest(!!response.data?.length);
      } catch (error) {
        setHasPendingRequest(false);
      }
    };

    checkPendingRequest();
  }, [user?.id]);

  const buildPreviewUrl = useCallback((imageUrl?: string | null, uploadedAt?: string | null) => {
    if (!imageUrl) return null;
    const encodedUrl = encodeURI(imageUrl);
    if (!uploadedAt) return encodedUrl;
    const separator = encodedUrl.includes('?') ? '&' : '?';
    return `${encodedUrl}${separator}v=${new Date(uploadedAt).getTime()}`;
  }, []);

  // Fetch existing horoscope image (nếu có) để hiển thị preview ban đầu
  useEffect(() => {
    const fetchHoroscope = async () => {
      if (!user?.id) return;

      try {
        const userId = parseInt(user.id.toString(), 10);
        if (isNaN(userId)) return;

        const horoscope = await usersService.getHoroscope(userId);
        if (horoscope?.imageUrl && !isHoroscopeDirty) {
          const previewUrl = buildPreviewUrl(horoscope.imageUrl, horoscope.uploadedAt ?? null);
          setHoroscopePreview(previewUrl);
          setHoroscopeSelectedCount(1);
        }
      } catch {
        // Im lặng nếu không có lá số hoặc lỗi – không chặn luồng chính
      }
    };

    fetchHoroscope();
  }, [user?.id, buildPreviewUrl, isHoroscopeDirty]);

  // Fetch existing id documents (nếu có) để hiển thị preview ban đầu
  useEffect(() => {
    const fetchIdCards = async () => {
      if (!user?.id) return;

      try {
        const userId = parseInt(user.id.toString(), 10);
        if (isNaN(userId)) return;

        const cards = await usersService.getIdCards(userId);
        const approvedCards = cards.filter(
          (card) => (card.status || '').toLowerCase() === 'approved',
        );

        const idCardFront = approvedCards.find(
          (card) => card.documentType === 'id_card_front',
        )?.fileUrl;
        const idCardBack = approvedCards.find(
          (card) => card.documentType === 'id_card_back',
        )?.fileUrl;
        const passportCover = approvedCards.find(
          (card) => card.documentType === 'passport_cover',
        )?.fileUrl;
        const passportPhoto = approvedCards.find(
          (card) => card.documentType === 'passport_additional' || card.documentType === 'passport_photo',
        )?.fileUrl;

        if (idCardFront && !isIdDocsDirty && !citizenIdFrontFile) {
          setCitizenIdFrontPreview(buildPreviewUrl(idCardFront, null));
        }
        if (idCardBack && !isIdDocsDirty && !citizenIdBackFile) {
          setCitizenIdBackPreview(buildPreviewUrl(idCardBack, null));
        }
        if (passportCover && !isIdDocsDirty && !passportCoverFile) {
          setPassportPreview(buildPreviewUrl(passportCover, null));
        }
        if (passportPhoto && !isIdDocsDirty && !passportPhotoFile) {
          setPassportAdditionalPreview(buildPreviewUrl(passportPhoto, null));
        }
      } catch {
        // Im lặng nếu không có giấy tờ hoặc lỗi – không chặn luồng chính
      }
    };

    fetchIdCards();
  }, [user?.id, buildPreviewUrl, isIdDocsDirty, citizenIdFrontFile, citizenIdBackFile, passportCoverFile, passportPhotoFile]);

  // Handler để hủy yêu cầu cập nhật đang pending
  const handleCancelUpdateRequest = async () => {
    if (!user?.id) {
      return;
    }

    try {
      const userId = parseInt(user.id.toString(), 10);
      if (isNaN(userId)) return;

      const response = await usersService.getUpdateRequests({
        userId,
        status: 'pending',
        limit: 1,
      });

      if (!response.data?.length) {
        return;
      }

      const requestId = response.data[0].id;
      const cancelResponse = await usersService.cancelUpdateRequest(requestId);

      if (cancelResponse.success) {
        setHasPendingRequest(false);
        window.location.href = '/personal/profile';
      }
    } catch (error: any) {
      console.error('Error canceling update request:', error);
    }
  };

  const handleSubmit = async () => {
    const result = await hookHandleSubmit();
    if (result === 'failed') return;
    const hasFiles = !!(horoscopeFile || citizenIdFrontFile || citizenIdBackFile || passportCoverFile || passportPhotoFile);
    if (result === 'no-changes' && !hasFiles && !certificateAttachmentUploaded) {
      message.warning('Không có thông tin nào được thay đổi');
      return;
    }

    if (horoscopeFile) {
      setHoroscopeUploading(true);
      try {
        const uploadResponse = await usersService.uploadHoroscope(horoscopeFile);
        if (uploadResponse?.success && uploadResponse.data?.url) {
          const previewUrl = buildPreviewUrl(uploadResponse.data.url, uploadResponse.data.uploadedAt);
          setHoroscopePreview(previewUrl);
          setHoroscopeSelectedCount(1);
          setIsHoroscopeDirty(false);
          setHoroscopeFile(null);
        } else {
          message.error('Không thể xác nhận ảnh lá số đã được gửi');
          return;
        }
      } catch (error: any) {
        console.error('Upload horoscope error:', error);
        message.error(error?.message || 'Không thể upload ảnh lá số tử vi. Vui lòng thử lại.');
        return;
      } finally {
        setHoroscopeUploading(false);
      }
    }

    const pendingUploads: Array<{
      file: File | null;
      documentType: string;
      side: 'front' | 'back';
    }> = [
      {
        file: citizenIdFrontFile,
        documentType: 'id_card_front',
        side: 'front',
      },
      {
        file: citizenIdBackFile,
        documentType: 'id_card_back',
        side: 'back',
      },
      {
        file: passportCoverFile,
        documentType: 'passport_cover',
        side: 'front',
      },
      {
        file: passportPhotoFile,
        documentType: 'passport_additional',
        side: 'back',
      },
    ];

    const hasIdDocs = pendingUploads.some((item) => item.file);
    if (hasIdDocs) {
      setIdDocsUploading(true);
    }

    try {
      for (const item of pendingUploads) {
        if (!item.file) continue;

        const uploadResponse = await usersService.uploadIdCard(
          item.file,
          item.documentType,
          item.side,
        );

        if (!uploadResponse?.success) {
          message.error(uploadResponse?.message || 'Không thể upload ảnh giấy tờ. Vui lòng thử lại.');
          return;
        }
      }

      if (hasIdDocs) {
        setIsIdDocsDirty(false);
        setCitizenIdFrontFile(null);
        setCitizenIdBackFile(null);
        setPassportCoverFile(null);
        setPassportPhotoFile(null);
        setCitizenIdFrontPreview(null);
        setCitizenIdBackPreview(null);
        setPassportPreview(null);
        setPassportAdditionalPreview(null);
        message.success('Đã gửi yêu cầu cập nhật giấy tờ, chờ phê duyệt.');
      }
    } catch (error: any) {
      console.error('Upload id documents error:', error);
      message.error(error?.message || 'Không thể upload ảnh giấy tờ. Vui lòng thử lại.');
      return;
    } finally {
      if (hasIdDocs) {
        setIdDocsUploading(false);
      }
    }
    window.location.href = '/personal/profile';
  };

  const validateImageFile = (file: File): boolean => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Vui lòng chọn file hình ảnh (JPG, PNG, WebP, ...)');
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      message.error('Kích thước ảnh tối đa 5MB');
      return false;
    }

    return true;
  };

  const previewImageFile = (file: File, onPreview: (previewUrl: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => {
      onPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleHoroscopeBeforeUpload = useCallback(async (file: File): Promise<boolean> => {
    if (!validateImageFile(file)) {
      return false;
    }

    setHoroscopeUploading(true);

    try {
      previewImageFile(file, (previewUrl) => {
        setHoroscopePreview(previewUrl);
        setHoroscopeSelectedCount(1);
        setIsHoroscopeDirty(true);
      });

      // Chỉ lưu file tạm để upload khi bấm "Cập nhật"
      setHoroscopeFile(file);
      message.info('Ảnh lá số đã được chọn. Bấm "Cập nhật" để gửi yêu cầu.');

      setIsHoroscopeDirty(true);
    } catch (error: any) {
      console.error('Upload horoscope error:', error);
      message.error(error?.message || 'Không thể upload ảnh lá số tử vi. Vui lòng thử lại.');
    } finally {
      setHoroscopeUploading(false);
    }

    // Ngăn Ant Design tự upload
    return false;
  }, []);

  const createIdDocBeforeUploadHandler = useCallback(
    (
      setPreview: (value: string | null) => void,
      setFile: (value: File | null) => void,
      label: string,
    ) =>
      async (file: File): Promise<boolean> => {
        if (!validateImageFile(file)) {
          return false;
        }

        setIdDocsUploading(true);

        try {
          previewImageFile(file, (previewUrl) => {
            setPreview(previewUrl);
            setIsIdDocsDirty(true);
          });

          setFile(file);
          message.info(`${label} đã được chọn. Bấm "Cập nhật" để gửi yêu cầu.`);
        } catch (error: any) {
          console.error('Preview id documents error:', error);
          message.error(error?.message || 'Không thể tải ảnh giấy tờ. Vui lòng thử lại.');
        } finally {
          setIdDocsUploading(false);
        }

        return false;
      },
    [],
  );

  const menuItems: MenuProps['items'] = [
    {
      key: 'personal-info',
      icon: <User size={22} />,
      label: 'Thông tin cá nhân',
    },
    {
      key: 'horoscope',
      icon: <Wallet size={22} />,
      label: 'Lá số',
    },
    {
      key: 'id-documents',
      icon: <CreditCard size={22} />,
      label: 'Giấy tờ tùy thân',
    },
    {
      key: 'contact-info',
      icon: <Phone size={22} />,
      label: 'Thông tin liên hệ',
    },
    {
      key: 'work-info',
      icon: <Briefcase size={22} />,
      label: 'Thông tin công việc',
    },
    {
      key: 'family',
      icon: <Users size={22} />,
      label: 'Gia đình và người phụ thuộc',
    },
    {
      key: 'education',
      icon: <GraduationCap size={22} />,
      label: 'Quá trình học tập',
    },
    {
      key: 'bank-info',
      icon: <Building2 size={22} />,
      label: 'Thông tin ngân hàng',
    },
    {
      key: 'certificates',
      icon: <Award size={22} />,
      label: 'Chứng chỉ & Kỹ năng',
    },
    {
      key: 'party-history',
      icon: <BookOpen size={22} />,
      label: 'Lịch sử Đảng viên',
    },
    {
      key: 'file-storage',
      icon: <FolderOpen size={22} />,
      label: 'Kho lưu trữ file cá nhân',
    },
  ];

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <div className={styles.headerLeft} onClick={handleCancel}>
            <ArrowLeft size={20} className={styles.backIcon} />
            <h2 className={styles.pageTitle}>Cập nhật hồ sơ cá nhân</h2>
          </div>
          <div className={styles.headerActions}>
            {hasPendingRequest && (
              <Button size="large" onClick={handleCancelUpdateRequest} className={styles.cancelUpdateRequestButton} icon={<XCircle size={16} />}>
                Hủy yêu cầu cập nhật
              </Button>
            )}
            <Button size="large" onClick={handleCancel} className={styles.cancelButton}>
              Hủy
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={loading || horoscopeUploading || idDocsUploading}
              disabled={initialDataStatus !== 'ready'}
              className={styles.submitButton}
              icon={<Save size={16} />}
            >
              Cập nhật
            </Button>
          </div>
        </div>
        <UpdateProfileLayout
          sidebarCollapsed={sidebarCollapsed}
          isHovered={isHovered}
          setIsHovered={setIsHovered}
          setSidebarCollapsed={setSidebarCollapsed}
          selectedMenuKey={selectedMenuKey}
          menuItems={menuItems}
          onMenuClick={handleMenuClick}
        >
        <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleValuesChange} className={styles.form}>
          {initialDataStatus !== 'ready' && (
            <Alert
              type={initialDataStatus === 'error' ? 'error' : 'info'}
              showIcon
              message={initialDataStatus === 'error'
                ? 'Không tải được đầy đủ dữ liệu hồ sơ. Hãy tải lại trang trước khi cập nhật.'
                : 'Đang tải dữ liệu hồ sơ, học vấn và người thân...'}
              style={{ marginBottom: 16 }}
            />
          )}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {MENU_TITLES[selectedMenuKey] || 'Thông tin cá nhân'}
            </h3>
            
            {selectedMenuKey === 'personal-info' && (
              <>
            <UpdatePersonalInfoSection
              uploading={uploading}
              profileImage={profileImage}
              setProfileImage={setProfileImage}
              handleImageUpload={handleImageUpload}
            />
              </>
            )}
            
            {selectedMenuKey !== 'personal-info' && (
              <>
                {selectedMenuKey === 'horoscope' && (
                  <UpdateHoroscopeSection
                    horoscopePreview={horoscopePreview}
                    horoscopeUploading={horoscopeUploading}
                    horoscopeSelectedCount={horoscopeSelectedCount}
                    onClear={() => {
                      setHoroscopePreview(null);
                      setHoroscopeSelectedCount(0);
                      setHoroscopeFile(null);
                      setIsHoroscopeDirty(true);
                    }}
                    onBeforeUpload={handleHoroscopeBeforeUpload}
                  />
                )}
                {selectedMenuKey === 'id-documents' && (
                  <UpdateIdDocumentsSection
                    citizenIdFrontPreview={citizenIdFrontPreview}
                    citizenIdBackPreview={citizenIdBackPreview}
                    passportPreview={passportPreview}
                    passportAdditionalPreview={passportAdditionalPreview}
                    isUploading={idDocsUploading}
                    onBeforeUploadCitizenFront={createIdDocBeforeUploadHandler(
                      setCitizenIdFrontPreview,
                      setCitizenIdFrontFile,
                      'Ảnh CCCD mặt trước',
                    )}
                    onBeforeUploadCitizenBack={createIdDocBeforeUploadHandler(
                      setCitizenIdBackPreview,
                      setCitizenIdBackFile,
                      'Ảnh CCCD mặt sau',
                    )}
                    onBeforeUploadPassportCover={createIdDocBeforeUploadHandler(
                      setPassportPreview,
                      setPassportCoverFile,
                      'Ảnh hộ chiếu',
                    )}
                    onBeforeUploadPassportPhoto={createIdDocBeforeUploadHandler(
                      setPassportAdditionalPreview,
                      setPassportPhotoFile,
                      'Ảnh khác hộ chiếu',
                    )}
                    onClearCitizenFront={() => {
                      setCitizenIdFrontPreview(null);
                      setCitizenIdFrontFile(null);
                      setIsIdDocsDirty(true);
                    }}
                    onClearCitizenBack={() => {
                      setCitizenIdBackPreview(null);
                      setCitizenIdBackFile(null);
                      setIsIdDocsDirty(true);
                    }}
                    onClearPassportCover={() => {
                      setPassportPreview(null);
                      setPassportCoverFile(null);
                      setIsIdDocsDirty(true);
                    }}
                    onClearPassportPhoto={() => {
                      setPassportAdditionalPreview(null);
                      setPassportPhotoFile(null);
                      setIsIdDocsDirty(true);
                    }}
                  />
                )}
                {selectedMenuKey === 'contact-info' && <UpdateContactInfoSection />}
                {selectedMenuKey === 'work-info' && <UpdateWorkInfoSection form={form} />}
                {selectedMenuKey === 'family' && <UpdateFamilyDependentsSection form={form} />}
                {selectedMenuKey === 'education' && <UpdateEducationSection form={form} />}
                {selectedMenuKey === 'bank-info' && <UpdateBankInfoSection form={form} />}
                {selectedMenuKey === 'certificates' && <UpdateCertificatesSkillsSection form={form} onAttachmentUploaded={() => setCertificateAttachmentUploaded(true)} />}
                {selectedMenuKey === 'party-history' && <UpdatePartyHistorySection form={form} />}
                {selectedMenuKey === 'file-storage' && <UpdateFileStorageSection />}
              </>
            )}
          </div>
        </Form>
        </UpdateProfileLayout>
      </div>
    </div>
  );
};
